'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { getCachedWeatherWithAge } from '@/lib/weather';
import { checkPlantingConditions } from '@/lib/checkPlantingConditions';
import type { PlantingWarning } from '@/lib/checkPlantingConditions';
import type { Plant } from '@/data/plants';

interface PlantLogButtonProps {
  plant: Plant;
}

export default function PlantLogButton({ plant }: PlantLogButtonProps) {
  const router = useRouter();
  const [warning, setWarning] = useState<PlantingWarning | null>(null);

  const destination = `/garden?tab=my-plants&plant=${plant.id}`;

  function handleClick() {
    const cached = getCachedWeatherWithAge();
    const w = checkPlantingConditions(plant, cached?.data ?? null);
    if (w) {
      setWarning(w);
    } else {
      router.push(destination);
    }
  }

  function proceed() {
    router.push(destination);
  }

  return (
    <div className="flex flex-col gap-2">
      {!warning && (
        <button
          onClick={handleClick}
          className="inline-flex h-9 items-center gap-1.5 font-body text-[13px] font-semibold
                     text-sage border border-sand px-3 rounded-lg
                     hover:bg-light-sage transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round">
            <path d="M6 1v10M1 6h10" />
          </svg>
          Log a Planting
        </button>
      )}

      {warning && (
        <div className={`rounded-lg px-4 py-3 flex gap-3 ${
          warning.severity === 'warning'
            ? 'bg-[#FEF3C7] border border-[#F59E0B]'
            : 'bg-light-sage border border-sage'
        }`}>
          <span className={`flex-shrink-0 mt-0.5 ${warning.severity === 'warning' ? 'text-[#F59E0B]' : 'text-sage'}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 1.5L14.5 13.5H1.5L8 1.5Z" />
              <path d="M8 6v3.5" />
              <circle cx="8" cy="11.5" r="0.6" fill="currentColor" stroke="none" />
            </svg>
          </span>
          <div className="flex-1">
            <p className={`font-body text-[14px] leading-relaxed ${
              warning.severity === 'warning' ? 'text-[#92400E]' : 'text-deep-green'
            }`}>
              {warning.message}
            </p>
            <div className="flex flex-wrap gap-2 mt-2.5">
              {warning.type === 'too-cold' && (
                <button
                  type="button"
                  onClick={proceed}
                  className="font-body text-[13px] font-semibold text-sage border border-sage
                             px-3 py-1.5 rounded-full hover:bg-light-sage transition-colors"
                >
                  I&apos;m starting indoors
                </button>
              )}
              {(warning.type === 'too-cold' || warning.type === 'too-hot') && (
                <button
                  type="button"
                  onClick={proceed}
                  className="font-body text-[13px] font-semibold text-sage border border-sage
                             px-3 py-1.5 rounded-full hover:bg-light-sage transition-colors"
                >
                  Log it anyway
                </button>
              )}
              {warning.type === 'hard-frost' && (
                <button
                  type="button"
                  onClick={proceed}
                  className="font-body text-[13px] font-semibold text-sage border border-sage
                             px-3 py-1.5 rounded-full hover:bg-light-sage transition-colors"
                >
                  I&apos;ll protect it
                </button>
              )}
              <button
                type="button"
                onClick={() => setWarning(null)}
                className="font-body text-[13px] text-sage hover:text-dark-text transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
